import { useArchiveSlideMutation } from "@api/slides-library";
import Spinner from "@components/ui/spinner";
import { cn } from "@lib/cn";
import {
  DialogBase,
  useRecentlyUsedStore,
} from "@modules/slides-library/shared";
import { useAppDispatch } from "@store";
import { closeDialog } from "@store/dialogs";
import { useCallback } from "react";
import { toast } from "react-toastify";
import Button from "@/components/ui/button";
import { handleAsyncOperation } from "@/shared";
import type { TSavedSlide } from "@/shared/types";

const DIALOG_ID = "delete-slide-dialog";

const SlideDeleteDialog = ({ slide }: { slide: TSavedSlide }) => {
  const dispatch = useAppDispatch();
  const [archiveSlide, { isLoading }] = useArchiveSlideMutation();

  const handleClose = useCallback(() => {
    dispatch(closeDialog(DIALOG_ID));
  }, [dispatch]);

  const handleSubmit = useCallback(() => {
    if (isLoading) return;
    handleAsyncOperation({
      operation: async () => {
        await archiveSlide({ id: slide._id }).unwrap();
        // Evict from recently-used so the deleted slide doesn't linger as a
        // ghost in the "Recently used" grid (monday 12796233935).
        useRecentlyUsedStore.getState().removeSavedSlide(slide._id);
        toast.success("Slide moved to recently deleted");
        handleClose();
      },
      onError: () => {
        toast.error("Failed to remove slide");
      },
    });
  }, [archiveSlide, handleClose, isLoading, slide._id]);

  return (
    <DialogBase>
      <DialogBase.Header title="Remove slide" onClose={handleClose} />
      <DialogBase.Content className="tw-pt-4">
        <p className="tw-m-0 tw-text-[13px] tw-font-normal tw-text-blue">
          Are you sure that you want to remove{" "}
          <span className="tw-font-semibold">“{slide.title}”</span> slide?
        </p>
      </DialogBase.Content>
      <DialogBase.Footer>
        <Button variant="secondary" className="tw-mr-4" onClick={handleClose}>
          Go back
        </Button>
        <Button
          onClick={handleSubmit}
          className={cn(isLoading && "tw-cursor-not-allowed")}
          variant="destructive"
        >
          {isLoading ? (
            <>
              <Spinner className="tw-mr-2 tw-size-3 tw-border-white" />
              Deleting...
            </>
          ) : (
            "Yes, remove"
          )}
        </Button>
      </DialogBase.Footer>
    </DialogBase>
  );
};

SlideDeleteDialog.id = DIALOG_ID;
export { SlideDeleteDialog };
